import React from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchServers } from "../lib/api";
import { useGame } from "../context/GameContext";
import { useFavorites } from "../context/FavoritesContext";
import { Skeleton } from "./ui/skeleton";
import { Button } from "./ui/button";
import { ArrowDown, ArrowUp, Inbox } from "lucide-react";
import { DesktopRow, MobileCard } from "./ServerRow";

const headerStyle = {
  color: "var(--text-dim)",
  fontFamily: "var(--font-mono)",
  letterSpacing: "0.18em",
  fontSize: "0.68rem",
  textTransform: "uppercase",
  textAlign: "left",
};

const SortableHeader = ({ label, sortKey, filters, setFilters }) => {
  const active = filters.sort === sortKey;
  const toggle = () => {
    if (active) setFilters((f) => ({ ...f, order: f.order === "asc" ? "desc" : "asc", page: 1 }));
    else setFilters((f) => ({ ...f, sort: sortKey, order: "desc", page: 1 }));
  };
  return (
    <th
      style={{ ...headerStyle, cursor: "pointer" }}
      className="py-3 px-3 select-none"
      onClick={toggle}
      data-testid={`sort-header-${sortKey}`}
    >
      <span className="inline-flex items-center gap-1" style={{ color: active ? "var(--primary-accent)" : undefined }}>
        {label}
        {active && (filters.order === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
      </span>
    </th>
  );
};

const ServerList = ({ filters, setFilters, onOpenServer, onDataChange }) => {
  const { gameId } = useGame();
  const { isFavorite, favIds, authed } = useFavorites();

  // Anonymous users: send their localStorage favorite IDs so the backend
  // can pin + filter them. Authenticated users already have Mongo state.
  const anonFavIdsCsv = !authed && favIds?.length ? favIds.join(",") : undefined;

  const params = {
    game_id: gameId,
    q: filters.q || undefined,
    region: filters.region !== "all" ? filters.region : undefined,
    mode: filters.mode !== "all" ? filters.mode : undefined,
    map: filters.map !== "all" ? filters.map : undefined,
    modifier: filters.modifier && filters.modifier !== "all" ? filters.modifier : undefined,
    official: filters.official !== "all" ? filters.official : undefined,
    min_players: filters.min_players || undefined,
    max_ping: filters.max_ping,
    online_only: filters.online_only,
    favorites_only: filters.favorites_only || undefined,
    pin_favorites: filters.pin_favorites === false ? false : undefined,
    fav_ids: anonFavIdsCsv,
    sort: filters.sort,
    order: filters.order,
    page: filters.page,
    page_size: filters.page_size,
  };

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["servers", params],
    queryFn: () => fetchServers(params),
    enabled: !!gameId,
    refetchInterval: 25000,
    keepPreviousData: true,
    // Any filter toggle must trigger a visible refetch. Without this override
    // the global 60s staleTime short-circuits returning to a previously-used
    // key (e.g. toggling 'online only' off → on within 60s), making the UI
    // appear frozen even though the key changed.
    staleTime: 0,
  });

  React.useEffect(() => {
    if (data) onDataChange?.(data);
  }, [data, onDataChange]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / filters.page_size)) : 1;

  return (
    <div className="tac-card rise relative" style={{ background: "var(--bg-surface)" }}>
      <span className="corner-tr" />
      <span className="corner-bl" />
      <div className="flex items-center justify-between px-4 py-2 border-b" style={{ borderColor: "var(--border-color)" }}>
        <div className="flex items-center gap-3">
          <span className="w-2 h-2 rounded-full" style={{ background: isFetching ? "var(--secondary-accent)" : "var(--highlight)" }} />
          <span className="hud-caption">
            {isFetching ? "SYNCING" : "LIVE"} · {data?.total ?? 0} MATCHES
          </span>
        </div>
        {data && <span className="hud-caption">PAGE {data.page}/{totalPages}</span>}
      </div>

      <div className="hidden md:block overflow-x-auto" data-testid="server-table">
        <table className="w-full border-collapse">
          <thead>
            <tr style={{ background: "var(--bg-elevated)" }}>
              <th style={headerStyle} className="py-3 px-3 w-6" />
              <th style={headerStyle} className="py-3 px-3 w-10">★</th>
              <SortableHeader label="Server Name" sortKey="name" filters={filters} setFilters={setFilters} />
              <SortableHeader label="Players" sortKey="players" filters={filters} setFilters={setFilters} />
              <SortableHeader label="Map" sortKey="map" filters={filters} setFilters={setFilters} />
              <SortableHeader label="Mode" sortKey="mode" filters={filters} setFilters={setFilters} />
              <th style={headerStyle} className="py-3 px-3">Type</th>
              <SortableHeader label="Ping" sortKey="ping" filters={filters} setFilters={setFilters} />
            </tr>
          </thead>
          <tbody>
            {isLoading &&
              Array.from({ length: 8 }).map((_, i) => (
                <tr key={i} className="border-b" style={{ borderColor: "var(--border-color)" }}>
                  {Array.from({ length: 9 }).map((__, j) => (
                    <td key={j} className="py-3 px-3">
                      <Skeleton className="h-4" style={{ background: "var(--bg-elevated)" }} />
                    </td>
                  ))}
                </tr>
              ))}
            {!isLoading && data?.items?.map((s, idx) => {
              const items = data.items;
              const isFav = isFavorite(s.id);
              // "Last favorite" = this row is a favorite AND either (a) the
              // next row isn't a favorite, OR (b) this is the last row on the
              // current page AND there are more pages (fav list spills over).
              const nextNotFav = idx + 1 < items.length && !isFavorite(items[idx + 1].id);
              const isTailOfPage = idx === items.length - 1 && data.total > data.page_size;
              const isLastFav = isFav && !filters.favorites_only && (nextNotFav || isTailOfPage);
              return (
                <DesktopRow
                  key={s.id}
                  server={s}
                  onOpen={onOpenServer}
                  isLastFavorite={isLastFav}
                />
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="md:hidden p-3 space-y-3" data-testid="server-card-list">
        {isLoading && Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-28" style={{ background: "var(--bg-elevated)" }} />
        ))}
        {!isLoading && data?.items?.map((s, idx) => {
          const items = data.items;
          const isFav = isFavorite(s.id);
          const nextNotFav = idx + 1 < items.length && !isFavorite(items[idx + 1].id);
          const isTailOfPage = idx === items.length - 1 && data.total > data.page_size;
          const isLastFav = isFav && !filters.favorites_only && (nextNotFav || isTailOfPage);
          return (
            <MobileCard key={s.id} server={s} onOpen={onOpenServer} isLastFavorite={isLastFav} />
          );
        })}
      </div>

      {!isLoading && (data?.items?.length ?? 0) === 0 && (
        <div className="py-16 flex flex-col items-center gap-4" data-testid="empty-state">
          <Inbox className="w-10 h-10" style={{ color: "var(--text-dim)" }} />
          <div className="hud-caption">NO SERVERS MATCH YOUR FILTERS</div>
          <Button
            variant="outline"
            onClick={() =>
              setFilters((f) => ({
                ...f, q: "", region: "all", mode: "all", map: "all",
                official: "all", min_players: 0, max_ping: 300,
                favorites_only: false, page: 1,
              }))
            }
            data-testid="empty-reset"
            style={{ background: "var(--bg-elevated)", borderColor: "var(--primary-accent)", color: "var(--primary-accent)", borderRadius: 2 }}
          >
            RESET FILTERS
          </Button>
        </div>
      )}

      {data && data.total > filters.page_size && (
        <div className="flex items-center justify-between px-4 py-3 border-t" style={{ borderColor: "var(--border-color)" }} data-testid="pagination">
          <span className="hud-caption">
            SHOWING {(data.page - 1) * data.page_size + 1}–{Math.min(data.page * data.page_size, data.total)} / {data.total}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={data.page <= 1} onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))} data-testid="page-prev"
              style={{ background: "var(--bg-elevated)", borderColor: "var(--border-strong)", color: "var(--text-primary)", borderRadius: 2 }}>
              PREV
            </Button>
            <Button variant="outline" size="sm" disabled={data.page >= totalPages} onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))} data-testid="page-next"
              style={{ background: "var(--bg-elevated)", borderColor: "var(--border-strong)", color: "var(--text-primary)", borderRadius: 2 }}>
              NEXT
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServerList;
